import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { PdfResponse } from 'types/pdf';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';
import { buildCdnParams } from 'containers/CarnetsDeNotes/Preview/Sidebar/buildCdnParams';
import {
  CarnetDeNotes,
  CarnetDeNotesPdfSettings,
} from 'containers/CarnetsDeNotes/types/carnetsDeNotes';
import { UserLocale } from 'types/user';
import { GroupBy } from 'containers/CarnetsDeNotes/CarnetDetails/types';
import { buildCdnEleveParams } from '../CarnetEleve/Preview/buildCdnEleveParams';

interface Props {
  schoolyearId: string;
  schoolId: string;
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
  eleveId?: string;
  groupBy: GroupBy;
  lng: UserLocale;
  settings: CarnetDeNotesPdfSettings;
  eleveFamilyName?: string;
  carnetDeNotes: CarnetDeNotes;
  logoPath?: string;
}

export const fetchCarnetDeNoteAPI = (s3Url, carnetDeNotesName, familyName) =>
  fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(
        response,
        familyName
          ? `${carnetDeNotesName}-${familyName}.pdf`
          : `${carnetDeNotesName}.pdf`,
      );
    });

export async function downloadCarnet(props: Props) {
  const {
    schoolyearId,
    schoolId,
    onStart,
    onFinished,
    onError,
    carnetDeNotes,
    eleveId,
    eleveFamilyName,
    logoPath,
  } = props;

  onStart();

  const params = eleveId
    ? buildCdnEleveParams({
        lng: props.lng,
        settings: props.settings,
        groupBy: props.groupBy,
        displayGraphs: carnetDeNotes.displayGraphs,
        displayCompetences: carnetDeNotes.displayCompetences,
        displayMeans: carnetDeNotes.displayMeans,
        displayDomaines: carnetDeNotes.displayDomaines,
        filterByNotes: carnetDeNotes.filterByNotes,
        logoPath,
      })
    : buildCdnParams({
        lng: props.lng,
        settings: props.settings,
        groupBy: props.groupBy,
      });
  if (props.eleveId) {
    params.append('eleveId', props.eleveId);
  }

  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${
    carnetDeNotes.id
  }/generate?${params.toString()}`;
  try {
    const response: PdfResponse = await request(requestURL, {
      method: 'GET',
    });
    const token = response.pdf_token;
    await fetchCarnetDeNoteAPI(token, carnetDeNotes.name, eleveFamilyName);
  } catch {
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadCarnet() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    carnetDeNotes,
    groupBy,
    settings,
    eleveFamilyName,
    eleveId,
    lng,
  }: {
    carnetDeNotes: CarnetDeNotes;
    eleveFamilyName?: string;
    settings: CarnetDeNotesPdfSettings;
    groupBy: GroupBy;
    eleveId?: string;
    lng: UserLocale;
  }) =>
    downloadCarnet({
      carnetDeNotes,
      lng,
      groupBy,
      eleveId,
      settings,
      schoolyearId: schoolyear.id,
      schoolId: school.id,
      eleveFamilyName,
      logoPath: school?.picture,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
